<script setup>
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'
import { tutors } from '@/routes/index'

const props = defineProps({
  subjects: {
    type: Array,
    default: () => []
  }
})

// Generate a lighter background color from the subject's color
const getLighterColor = (hexColor) => {
  if (!hexColor) return 'rgb(243 244 246)' // gray-100 fallback

  // Convert hex to RGB
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Mix with white for lighter shade (90% white, 10% color)
  const lightR = Math.round(r * 0.15 + 255 * 0.85)
  const lightG = Math.round(g * 0.15 + 255 * 0.85)
  const lightB = Math.round(b * 0.15 + 255 * 0.85)

  return `rgb(${lightR} ${lightG} ${lightB})`
}

// Add "View all" option at the end
const displaySubjects = computed(() => {
  return [
    ...props.subjects,
    {
      id: 'view-all',
      name: 'View all',
      slug: null,
      icon_name: null,
      color: '#6B7280' // gray-500
    }
  ]
})

const getSubjectUrl = (subject) => {
  if (subject.slug) {
    return tutors.url({ query: { subject: subject.slug } })
  }
  return tutors.url()
}
</script>

<template>
  <section class="py-12 md:py-24 bg-white">
    <div class="max-w-7xl mx-auto px-4 md:px-8">
      <div class="text-center mb-8 md:mb-16">
        <h2 class="text-3xl md:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Find tutors for any subject
        </h2>
        <p class="text-base md:text-xl text-gray-600">
          Expert tutors across GCSE, A-Level, and beyond
        </p>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
        <Link
          v-for="subject in displaySubjects"
          :key="subject.id"
          :href="getSubjectUrl(subject)"
          class="rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer"
          :style="{ backgroundColor: getLighterColor(subject.color) }"
        >
          <div class="w-14 h-14 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 shadow-sm">
            <span
              v-if="subject.slug"
              class="material-symbols-outlined text-3xl"
              :style="{ color: subject.color || '#6B7280' }"
            >
              {{ subject.icon_name || 'school' }}
            </span>
            <span
              v-else
              class="material-symbols-outlined text-3xl text-gray-600"
            >
              more_horiz
            </span>
          </div>
          <p class="font-bold text-gray-900">{{ subject.name }}</p>
        </Link>
      </div>
    </div>
  </section>
</template>
